/****************************************************************************
 * Copyright 2021 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/

import { Field } from '../../component/form/form/form';
import { getSelectOptionsFromSchema } from '../../utils';
import Select from '../../component/form/Select';
import { sdataCustomSchema } from '../../data/schema/sdata-schema';

interface SdataFormResult {
  context: string;
  fieldName: string;
  fieldValue: string;
  radiobuttons: boolean;
}

interface SDataFieldsetFormState {
  errors: Record<string, unknown>;
  valid: boolean;
  result: SdataFormResult;
}

interface SDataFieldsetProps {
  formState: SDataFieldsetFormState;
}

const content = (
  schema: typeof sdataCustomSchema,
  context: string,
  fieldName: string,
  _fieldValue: string,
  checked: boolean,
  isContextEmpty: boolean,
) =>
  Object.keys(schema.properties)
    .filter(
      (prop) => prop !== 'type' && prop !== 'context' && prop !== 'fieldName',
    )
    .map((prop) => {
      if (prop === 'radiobuttons') {
        return (
          <Field
            name={prop}
            checked={checked}
            type="radio"
            key={`${context}-${fieldName}-${prop}-radio`}
            labelPos={false}
            disabled={isContextEmpty}
          />
        );
      } else if (prop === 'fieldValue') {
        return (
          <Field
            name={prop}
            key={`${context}-${fieldName}-${prop}-select`}
            placeholder="Enter value"
            disabled={isContextEmpty}
          />
        );
      } else {
        return (
          <Field
            name={prop}
            type="textarea"
            key={`${context}-${fieldName}-${prop}-select`}
            disabled={isContextEmpty}
          />
        );
      }
    });

function SDataFieldset({ formState }: SDataFieldsetProps) {
  const { result } = formState;
  const formSchema = sdataCustomSchema;
  const validContextValues = formSchema.properties.context.enum;
  const isContextEmpty =
    !result.context || !validContextValues.includes(result.context);

  return (
    <fieldset className="sdata">
      <Field
        name="context"
        options={getSelectOptionsFromSchema(formSchema.properties.context)}
        component={Select}
        data-testid="context"
      />
      <Field
        name="fieldName"
        placeholder="Enter name"
        disabled={isContextEmpty}
      />
      {content(
        formSchema,
        result.context,
        result.fieldName,
        result.fieldValue,
        result.radiobuttons,
        isContextEmpty,
      )}
    </fieldset>
  );
}

export default SDataFieldset;
